import { useEffect, useRef } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  jsonLd?: object | object[];
}

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogUrl,
  jsonLd,
}: SEOProps) {
  const scriptRefs = useRef<HTMLScriptElement[]>([]);

  useEffect(() => {
    scriptRefs.current.forEach((s) => {
      if (document.head.contains(s)) document.head.removeChild(s);
    });
    scriptRefs.current = [];

    if (!jsonLd) return;
    const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      scriptRefs.current.push(script);
    });

    return () => {
      scriptRefs.current.forEach((s) => {
        if (document.head.contains(s)) document.head.removeChild(s);
      });
      scriptRefs.current = [];
    };
  }, [jsonLd]);

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {canonical && <link rel="canonical" href={canonical} />}
      <meta property="og:title" content={ogTitle ?? title} />
      <meta property="og:description" content={ogDescription ?? description} />
      <meta property="og:type" content="website" />
      {ogUrl && <meta property="og:url" content={ogUrl} />}
    </>
  );
}
